/**
 * `/:world/quiz`: which series to start with.
 *
 * All the questions on one page, each a <fieldset> of real radio buttons, so
 * the reader sees the length from the page itself and a keyboard reaches
 * every answer. A bar at the bottom counts the answers and submits; from
 * MIN_ANSWERS on, a second button offers the result early. There is no
 * default answer: a question left alone scores nothing.
 *
 * The answers live in the address (`?a=dacca-----`), updated without a reload
 * on every change, so the back button and a copied link both work, and a link
 * someone posts opens on its result. The scoring is src/shared/quiz.ts, which
 * the Worker also runs to write a shared result into the page's title.
 *
 * The result names two series, never one, says how close they are, why the
 * first, and which book to start with, and always offers all of them.
 */

import { Fragment, useEffect, useRef, useState, useSyncExternalStore, type FormEvent, type ReactNode, type RefObject } from 'react'
import { Link, useSearchParams } from 'react-router'
import {
  answeredCount,
  encodeAnswers,
  joinReasons,
  MIN_ANSWERS,
  parseAnswers,
  quizResult,
  type Answers,
  type Quiz as QuizContent,
  type QuizResult,
} from '../../shared/quiz.ts'
import { quizFor } from '../../shared/quizzes/index.ts'
import { worldPath } from '../../shared/routes.ts'
import { Alert } from '../components/Alert.tsx'
import { CataloguePage, usePageTitle, WorldNotFound } from '../components/Catalogue.tsx'
import { useI18n } from '../i18n/index.tsx'
import { fill, mainTitle, note, seriesName, workBySlug, type Loaded } from '../lib/catalogue.ts'
import { useShelf } from '../lib/shelf.tsx'

export function Quiz() {
  return (
    <CataloguePage>
      {(catalogue, world) => {
        const quiz = quizFor(world)
        return quiz === null ? <WorldNotFound /> : <QuizView catalogue={catalogue} world={world} quiz={quiz} />
      }}
    </CataloguePage>
  )
}

/** A template with `{name}` placeholders, filled with elements rather than strings. */
function fillNodes(template: string, values: Record<string, ReactNode>): ReactNode {
  return template.split(/(\{\w+\})/).map((part, index) => {
    const name = /^\{(\w+)\}$/.exec(part)?.[1]
    return <Fragment key={index}>{name !== undefined && name in values ? values[name] : part}</Fragment>
  })
}

function QuizView({ catalogue, world, quiz }: { catalogue: Loaded; world: string; quiz: QuizContent }) {
  const { t, locale } = useI18n()
  usePageTitle(t('quiz.title'))
  const [params, setParams] = useSearchParams()
  const [answers, setAnswers] = useState<Answers>(() => parseAnswers(quiz, params.get('a')))
  // A link that arrives with enough answers is a shared result: open on it.
  const [shown, setShown] = useState(() => quizResult(quiz, answers) !== null)
  const [tooFew, setTooFew] = useState(false)
  const resultHeading = useRef<HTMLHeadingElement>(null)
  const [focusResult, setFocusResult] = useState(false)
  // A signed-in reader's result is saved when they ask for it (not on a shared
  // link, which is someone else's). Signed out, nothing is said about accounts:
  // the quiz never asks for one.
  const { saveQuiz } = useShelf()
  const [saved, setSaved] = useState(false)

  const total = quiz.questions.length
  const answered = answeredCount(answers)
  const result = shown ? quizResult(quiz, answers) : null

  useEffect(() => {
    if (focusResult && resultHeading.current !== null) {
      resultHeading.current.focus()
      resultHeading.current.scrollIntoView({ block: 'start' })
      setFocusResult(false)
    }
  }, [focusResult])

  function choose(question: number, option: number) {
    const next = answers.map((answer, index) => (index === question ? option : answer))
    setAnswers(next)
    setTooFew(false)
    // Replace, not push: the back button leaves the quiz rather than undoing
    // one answer at a time.
    setParams({ a: encodeAnswers(next) }, { replace: true })
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (answered < MIN_ANSWERS) {
      setTooFew(true)
      return
    }
    setShown(true)
    setFocusResult(true)
    setSaved(false)
    void saveQuiz(encodeAnswers(answers)).then(setSaved)
  }

  return (
    <>
      <section className="quiz-intro">
        <p className="label">{fill(t('quiz.kicker'), { count: total })}</p>
        <h1>{t('quiz.title')}</h1>
        <p>{quiz.intro[locale]}</p>
      </section>

      {result === null ? null : (
        <Result
          catalogue={catalogue}
          world={world}
          quiz={quiz}
          result={result}
          total={total}
          link={`${worldPath(world, 'quiz')}?a=${encodeAnswers(answers)}`}
          headingRef={resultHeading}
          saved={saved}
        />
      )}

      <form className="quiz" onSubmit={onSubmit} noValidate>
        {quiz.questions.map((question, q) => (
          <fieldset className="quiz__question" key={q}>
            <legend>
              {/* The number in a medallion, as on a reading path. The medallion is
                  hidden from screen readers, which hear „1. …“ from the text. */}
              <span className="quiz__number" aria-hidden="true">
                {q + 1}
              </span>
              <span className="quiz__prompt">
                <span className="visually-hidden">{`${String(q + 1)}. `}</span>
                {question.prompt[locale]}
              </span>
            </legend>
            {question.options.map((option, o) => (
              <label className="quiz__option" key={o}>
                <input
                  type="radio"
                  name={`q${q + 1}`}
                  value={o}
                  checked={answers[q] === o}
                  onChange={() => {
                    choose(q, o)
                  }}
                />
                <span>{option.label[locale]}</span>
              </label>
            ))}
          </fieldset>
        ))}

        <div className="quiz__bar">
          <p className="quiz__count" aria-live="polite">
            {fill(t('quiz.count'), { answered, total })}
          </p>
          {tooFew ? <Alert tone="error">{fill(t('quiz.too_few'), { min: MIN_ANSWERS })}</Alert> : null}
          <div className="quiz__buttons">
            {/* Absent, not disabled, below the minimum and once every question is answered. */}
            {answered >= MIN_ANSWERS && answered < total ? (
              <button type="submit" className="button button--quiet">
                {t('quiz.enough')}
              </button>
            ) : null}
            <button type="submit" className="button">
              {t('quiz.submit')}
            </button>
          </div>
        </div>
      </form>
    </>
  )
}

function Result({
  catalogue,
  world,
  quiz,
  result,
  total,
  link,
  headingRef,
  saved,
}: {
  catalogue: Loaded
  world: string
  quiz: QuizContent
  result: QuizResult
  total: number
  link: string
  headingRef: RefObject<HTMLHeadingElement | null>
  saved: boolean
}) {
  const { t, locale } = useI18n()
  const seriesCount = catalogue.series.length
  const all = (
    <Link to={worldPath(world, 'series')}>{fill(t('quiz.result.all'), { count: seriesCount })}</Link>
  )
  const basedOn = result.answered < total ? <p className="quiz-result__meta">{fill(t('quiz.result.based_on'), { answered: result.answered, total })}</p> : null

  if (result.kind === 'none') {
    return (
      <section className="quiz-result panel" aria-labelledby="quiz-result-heading">
        <h2 id="quiz-result-heading" ref={headingRef} tabIndex={-1}>
          {t('quiz.result.heading')}
        </h2>
        <p>{t('quiz.result.none')}</p>
        {basedOn}
        <p>{all}</p>
      </section>
    )
  }

  const first = catalogue.series.find((series) => series.slug === result.first)
  const second = catalogue.series.find((series) => series.slug === result.second)
  if (first === undefined || second === undefined) return null
  const nameLink = (series: typeof first) => <Link to={worldPath(world, 'series', series.slug)}>{seriesName(series, locale)}</Link>
  const summary = fillNodes(t(`quiz.result.${result.kind}`), { first: nameLink(first), second: nameLink(second) })

  const bookLink = (slug: string) => {
    const work = workBySlug(catalogue, slug)
    return work === undefined ? null : <Link to={worldPath(world, 'books', work.slug)}>„{mainTitle(work, locale)}“</Link>
  }
  const special = quiz.starts?.[first.slug]
  const opening = first.books[0]
  let start: ReactNode = null
  if (special !== undefined) {
    const links = Object.fromEntries(special.works.map((slug, index) => [String.fromCharCode(97 + index), bookLink(slug)]))
    start = <p className="quiz-result__start">{fillNodes(special.text[locale], links)}</p>
  } else if (opening !== undefined) {
    const startNote = note(opening.note_bg, locale)
    start = (
      <>
        <p className="quiz-result__start">{fillNodes(t('quiz.result.start'), { book: bookLink(opening.work) })}</p>
        {startNote === null ? null : <p>{startNote}</p>}
      </>
    )
  }

  return (
    <section className="quiz-result panel" aria-labelledby="quiz-result-heading">
      <h2 id="quiz-result-heading" ref={headingRef} tabIndex={-1}>
        {t('quiz.result.heading')}
      </h2>
      <p className="quiz-result__summary">{summary}</p>
      {/* Keyed by the answers: a changed result starts with no "Копирано" showing. */}
      <Share key={link} path={link} />
      {result.reasons.length === 0 ? null : (
        <p>
          {fill(t('quiz.result.why'), {
            reasons: joinReasons(
              result.reasons.map((reason) => reason[locale]),
              t('quiz.result.and'),
            ),
          })}
        </p>
      )}
      {start}
      {basedOn}
      {saved ? <p className="quiz-result__meta">{t('quiz.result.saved')}</p> : null}
      <p className="panel__links">{all}</p>
    </section>
  )
}

const noSubscription = () => () => {}
const browserOrigin = () => window.location.origin
const serverOrigin = () => ''

/**
 * Sharing the result: the site's one way of being found other than search,
 * so it is the first thing under the result, not a link at its foot. The old
 * link pointed at the page the reader was already on, and clicking it looked
 * like a reload that did nothing.
 *
 * A button copies the absolute address, answers and all, and says so. The
 * address is also shown, read-only and selectable, for when the clipboard is
 * refused (an old browser, a denied permission): the reader can still copy
 * it by hand, and the button says to.
 *
 * The address is made absolute in the browser, after the page has hydrated:
 * the server renders the path, and the origin it would write (a test run's
 * localhost, or production) is not one the server should guess.
 */
function Share({ path }: { path: string }) {
  const { t } = useI18n()
  // The server's snapshot is empty, so hydration matches; the browser's is its origin.
  const origin = useSyncExternalStore(noSubscription, browserOrigin, serverOrigin)
  const url = origin === '' ? path : new URL(path, origin).href
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const input = useRef<HTMLInputElement>(null)

  async function copy() {
    try {
      await navigator.clipboard.writeText(url)
      setState('copied')
    } catch {
      input.current?.select()
      setState('failed')
    }
  }

  return (
    <div className="quiz-share">
      <p className="quiz-share__title">{t('quiz.share.title')}</p>
      <div className="quiz-share__row">
        <button type="button" className="button" onClick={() => void copy()}>
          {t('quiz.share.copy')}
        </button>
        <span className="quiz-share__status" role="status">
          {state === 'copied' ? t('quiz.share.copied') : state === 'failed' ? t('quiz.share.failed') : ''}
        </span>
      </div>
      <label className="quiz-share__label" htmlFor="quiz-share-url">
        {t('quiz.share.label')}
      </label>
      <input
        ref={input}
        id="quiz-share-url"
        className="field__input quiz-share__url"
        type="text"
        readOnly
        value={url}
        onFocus={(event) => {
          event.target.select()
        }}
      />
    </div>
  )
}
